import { useCallback, useEffect, useRef, useState } from "react";
import {
  InvestigationAttachment,
  InvestigationAttachmentUploadPost,
  Stage,
} from "../lusca-search-api/generated-src";
import { InvestigationsApiFactory } from "../lusca-search-api/LuscaSearchApiFactory";
import {
  MAX_ATTACHMENTS,
  contentTypeFor,
  isImageType,
  validateFile,
} from "../components/investigations/attachments/attachmentUtils";
import { toApiError } from "./useInvestigations";

export interface PendingAttachment {
  id: string;
  fileName: string;
  contentType?: string;
  size?: number;
  status: "uploading" | "ready" | "error";
  /** 0..1 while uploading. */
  progress: number;
  error?: string;
  key?: string;
  /** Object URL for a local image (revoked on remove/unmount) or the signed URL of a saved one. */
  previewUrl?: string;
  /** Set for attachments that were already on the comment/conclusion. */
  saved?: InvestigationAttachment;
}

/** Browser → S3 with the presigned POST (S3 enforces the size and type). XHR for progress. */
export const uploadToS3 = (
  post: InvestigationAttachmentUploadPost,
  file: File,
  onProgress: (fraction: number) => void
): Promise<void> =>
  new Promise((resolve, reject) => {
    const form = new FormData();
    Object.entries(post.fields || {}).forEach(([k, v]) => form.append(k, v));
    form.append("file", file); // must be the last field
    const xhr = new XMLHttpRequest();
    xhr.open("POST", post.url);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total);
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(
            new Error(
              xhr.status === 400 && /EntityTooLarge/.test(xhr.responseText)
                ? "File is too large"
                : `Upload failed (${xhr.status})`
            )
          );
    xhr.onerror = () => reject(new Error("Upload failed (network)"));
    xhr.send(form);
  });

let counter = 0;
const nextId = () => {
  counter += 1;
  return `att-${Date.now()}-${counter}`;
};

const fromSaved = (a: InvestigationAttachment): PendingAttachment => ({
  id: a.key,
  fileName: a.fileName || a.key.split("/").pop() || "attachment",
  contentType: a.contentType,
  size: a.size,
  status: "ready",
  progress: 1,
  key: a.key,
  previewUrl: a.url,
  saved: a,
});

/**
 * Attachments being composed for one comment or conclusion: uploads start as soon as a file is
 * picked/pasted/dropped so the Save button only waits for whatever is still in flight.
 */
export function useAttachmentUploads(
  stage: Stage,
  investigationId: string,
  initial: InvestigationAttachment[] = [],
  uploader: typeof uploadToS3 = uploadToS3
) {
  const [items, setItems] = useState<PendingAttachment[]>(() =>
    initial.map(fromSaved)
  );
  const objectUrls = useRef<Set<string>>(new Set());
  const [rejected, setRejected] = useState<string[]>([]);

  const patch = (id: string, change: Partial<PendingAttachment>) =>
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...change } : item))
    );

  useEffect(
    () => () => {
      objectUrls.current.forEach((url) => URL.revokeObjectURL?.(url));
    },
    []
  );

  const add = useCallback(
    (files: File[]): PendingAttachment[] => {
      const problems: string[] = [];
      const accepted: PendingAttachment[] = [];
      const room = MAX_ATTACHMENTS - items.length;
      files.forEach((file, i) => {
        const problem =
          i >= room
            ? `${file.name}: at most ${MAX_ATTACHMENTS} attachments`
            : validateFile(file);
        if (problem) {
          problems.push(problem);
          return;
        }
        const contentType = contentTypeFor(file)!;
        let previewUrl: string | undefined;
        if (isImageType(contentType) && URL.createObjectURL) {
          previewUrl = URL.createObjectURL(file);
          objectUrls.current.add(previewUrl);
        }
        const item: PendingAttachment = {
          id: nextId(),
          fileName: file.name,
          contentType,
          size: file.size,
          status: "uploading",
          progress: 0,
          previewUrl,
        };
        accepted.push(item);
        const api = InvestigationsApiFactory();
        api
          .getInvestigationArtifactUploadUrl(stage, investigationId, {
            fileName: file.name,
            contentType,
            size: file.size,
          })
          .then(({ data }) => {
            if (!data.post) throw new Error("Upload is not available");
            return uploader(data.post, file, (progress) =>
              patch(item.id, { progress })
            ).then(() =>
              patch(item.id, {
                status: "ready",
                progress: 1,
                key: data.key,
                fileName: data.fileName || file.name,
              })
            );
          })
          .catch((err) =>
            patch(item.id, {
              status: "error",
              error: toApiError(err).message,
            })
          );
      });
      setRejected(problems);
      if (accepted.length) setItems((prev) => [...prev, ...accepted]);
      return accepted;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- patch is stable in effect (setState)
    [items.length, stage, investigationId, uploader]
  );

  const remove = useCallback((id: string) => {
    setItems((prev) => {
      const gone = prev.find((item) => item.id === id);
      if (gone?.previewUrl && objectUrls.current.has(gone.previewUrl)) {
        URL.revokeObjectURL?.(gone.previewUrl);
        objectUrls.current.delete(gone.previewUrl);
      }
      return prev.filter((item) => item.id !== id);
    });
  }, []);

  const reset = useCallback((next: InvestigationAttachment[] = []) => {
    objectUrls.current.forEach((url) => URL.revokeObjectURL?.(url));
    objectUrls.current.clear();
    setItems(next.map(fromSaved));
    setRejected([]);
  }, []);

  const uploading = items.some((item) => item.status === "uploading");
  const failed = items.some((item) => item.status === "error");
  /** What to send: `[{key}]` of every ready file, in order. */
  const refs = items
    .filter((item) => item.status === "ready" && item.key)
    .map((item) => ({ key: item.key! }));

  return {
    items,
    add,
    remove,
    reset,
    uploading,
    failed,
    refs,
    rejected,
    dismissRejected: () => setRejected([]),
  };
}

export type AttachmentUploads = ReturnType<typeof useAttachmentUploads>;
